import React, { ReactElement, useEffect, useMemo, useRef } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import { StatusCell, TextFilter } from "./cells";
import { IRowStatus } from "./useSavedRows";

interface IProps {
	/** Must be memoised by the page, or every render rebuilds the table */
	columns: object[];
	data: object[];
	statuses: Record<number, IRowStatus>;
	saveRow: (id: number, changes: object) => void;
	/** Turns on the checkbox column when given */
	selectedIds?: Set<number>;
	onSelectionChange?: (ids: Set<number>) => void;
}

const PAGE_SIZES = [25, 50, 100];

/**
 * The editable table behind the Products, Bundles and Libraries pages.
 *
 * Filters, sorting and the page stay put while rows are edited, and only the
 * current page is drawn. Cells get saveRow and statuses from the table
 * instance, as react-table's editable example passes updateMyData.
 */
export const AdminTable = (props: IProps): ReactElement => {
	const { selectedIds, onSelectionChange } = props;
	const selectable = !!(selectedIds && onSelectionChange);

	const defaultColumn = useMemo(() => ({ Filter: TextFilter }), []);

	const columns = useMemo(() => {
		const status = {
			id: "status",
			Header: "",
			width: "2.5rem",
			disableFilters: true,
			disableSortBy: true,
			Cell: StatusCell,
		};

		return [...props.columns, status];
	}, [props.columns]);

	const table: any = useTable(
		{
			columns,
			data: props.data,
			defaultColumn,
			getRowId: (row: any) => String(row.id),
			autoResetFilters: false,
			autoResetSortBy: false,
			autoResetPage: false,
			initialState: { pageSize: 50 },
			saveRow: props.saveRow,
			statuses: props.statuses,
		} as any,
		useFilters,
		useSortBy,
		usePagination,
	);

	const {
		headerGroups,
		rows,
		page,
		prepareRow,
		pageCount,
		gotoPage,
		setPageSize,
		state: { pageIndex, pageSize, filters },
	} = table;

	// A new search starts from the first page
	const filterKey = JSON.stringify(filters);
	const firstRender = useRef(true);
	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false;
			return;
		}
		gotoPage(0);
	}, [filterKey]);

	// Don't strand the view past the last page when rows go away
	useEffect(() => {
		if (pageCount > 0 && pageIndex >= pageCount) {
			gotoPage(pageCount - 1);
		}
	}, [pageCount, pageIndex]);

	const matchingIds: number[] = rows.map((row: any) => row.original.id);
	const selectedMatching = selectable
		? matchingIds.filter(id => selectedIds!.has(id)).length
		: 0;

	const toggleAll = () => {
		const next = new Set(selectedIds);
		if (selectedMatching === matchingIds.length) {
			matchingIds.forEach(id => next.delete(id));
		} else {
			matchingIds.forEach(id => next.add(id));
		}
		onSelectionChange!(next);
	};

	const toggleOne = (id: number) => {
		const next = new Set(selectedIds);
		next.has(id) ? next.delete(id) : next.add(id);
		onSelectionChange!(next);
	};

	const first = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
	const last = Math.min(rows.length, (pageIndex + 1) * pageSize);

	return (
		<>
			<Table striped bordered hover className="admin-table mb-0">
				<thead>
					{headerGroups.map((headerGroup: any) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{selectable && (
								<th className="admin-select-cell">
									<Form.Check
										aria-label={`Select all ${
											matchingIds.length
										} matching`}
										title={`Select all ${
											matchingIds.length
										} matching`}
										checked={
											matchingIds.length > 0 &&
											selectedMatching ===
												matchingIds.length
										}
										ref={(
											input: HTMLInputElement | null,
										) => {
											if (input) {
												input.indeterminate =
													selectedMatching > 0 &&
													selectedMatching <
														matchingIds.length;
											}
										}}
										onChange={toggleAll}
									/>
								</th>
							)}
							{headerGroup.headers.map((column: any) => (
								<th
									{...column.getHeaderProps()}
									width={column.width}
								>
									<span
										{...column.getSortByToggleProps()}
										className={
											column.canSort
												? "admin-sortable"
												: undefined
										}
									>
										{column.render("Header")}
										{column.isSorted && (
											<i
												className={`fa fa-sort-${
													column.isSortedDesc
														? "desc"
														: "asc"
												} ms-1`}
											/>
										)}
									</span>
									{column.canFilter &&
										column.render("Filter")}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{page.length === 0 && (
						<tr>
							<td
								colSpan={columns.length + (selectable ? 1 : 0)}
								className="text-center text-muted py-4"
							>
								Nothing matches your search.
							</td>
						</tr>
					)}
					{page.map((row: any) => {
						prepareRow(row);
						const id = row.original.id;
						const selected = selectable && selectedIds!.has(id);

						return (
							<tr
								{...row.getRowProps()}
								className={
									selected ? "admin-row-selected" : undefined
								}
							>
								{selectable && (
									<td className="admin-select-cell">
										<Form.Check
											aria-label="Select row"
											checked={selected}
											onChange={() => toggleOne(id)}
										/>
									</td>
								)}
								{row.cells.map((cell: any) => (
									<td {...cell.getCellProps()}>
										{cell.render("Cell")}
									</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<div className="admin-table-footer">
				<span className="text-muted">
					{first}&ndash;{last} of {rows.length}
					{rows.length !== props.data.length &&
						` (filtered from ${props.data.length})`}
				</span>
				<div className="admin-table-pager">
					<Form.Select
						size="sm"
						value={pageSize}
						aria-label="Rows per page"
						onChange={event =>
							setPageSize(Number(event.target.value))
						}
					>
						{PAGE_SIZES.map(size => (
							<option key={size} value={size}>
								{size} per page
							</option>
						))}
					</Form.Select>
					<Button
						size="sm"
						variant="outline-secondary"
						disabled={!table.canPreviousPage}
						onClick={() => table.previousPage()}
						aria-label="Previous page"
					>
						<i className="fa fa-chevron-left" />
					</Button>
					<span className="text-muted text-nowrap">
						Page {pageCount === 0 ? 0 : pageIndex + 1} of{" "}
						{pageCount}
					</span>
					<Button
						size="sm"
						variant="outline-secondary"
						disabled={!table.canNextPage}
						onClick={() => table.nextPage()}
						aria-label="Next page"
					>
						<i className="fa fa-chevron-right" />
					</Button>
				</div>
			</div>
		</>
	);
};
